import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../../../services/api';
import type { Chat, QuickReply } from '../../../types';

type Props = {
  activeChat: Chat | null;
};

const QuickRepliesPanel = ({ activeChat }: Props) => {
  const [replies, setReplies] = useState<QuickReply[]>([]);
  const [loading, setLoading] = useState(true);
  const [shortcut, setShortcut] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const loadedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const data = await api.quickReplies();
      setReplies(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void load();
  }, [load]);

  const handleSave = async () => {
    if (!shortcut.trim() || !content.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        const updated = await api.updateQuickReply(editingId, {
          shortcut: shortcut.trim(),
          content: content.trim(),
        });
        setReplies((current) =>
          current.map((r) => (r.id === updated.id ? updated : r)),
        );
      } else {
        const created = await api.createQuickReply({
          shortcut: shortcut.trim(),
          content: content.trim(),
        });
        setReplies((current) => [...current, created]);
      }
      setShortcut('');
      setContent('');
      setEditingId(null);
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (reply: QuickReply) => {
    setEditingId(reply.id);
    setShortcut(reply.shortcut);
    setContent(reply.content);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.deleteQuickReply(id);
      setReplies((current) => current.filter((r) => r.id !== id));
      if (editingId === id) {
        setEditingId(null);
        setShortcut('');
        setContent('');
      }
    } catch {
      // ignore
    }
  };

  const handleInsert = async (reply: QuickReply) => {
    if (!activeChat) return;
    try {
      await api.sendMessage(activeChat.id, {
        content: reply.content,
        clientMessageId: `qr:${crypto.randomUUID()}`,
      });
    } catch {
      // error shown globally
    }
  };

  return (
    <div className="quick-replies-panel">
      <p className="tool-description">
        Sla veelgebruikte antwoorden op met een korte code. Klik op een reply om het direct te
        versturen in de actieve chat.
      </p>
      {loading && <p className="muted small">Laden…</p>}
      {!loading && replies.length === 0 && (
        <p className="empty-state small">Nog geen quick replies. Maak er een aan hieronder.</p>
      )}
      <div className="quick-reply-list">
        {replies.map((reply) => (
          <div key={reply.id} className="quick-reply-item">
            <div className="quick-reply-content">
              <code className="quick-reply-shortcut">/{reply.shortcut}</code>
              <p className="quick-reply-text">{reply.content}</p>
            </div>
            <div className="quick-reply-actions">
              {activeChat && (
                <button
                  type="button"
                  className="button secondary small"
                  onClick={() => void handleInsert(reply)}
                  title="Versturen in actieve chat"
                >
                  →
                </button>
              )}
              <button
                type="button"
                className="button secondary small"
                onClick={() => handleEdit(reply)}
              >
                ✎
              </button>
              <button
                type="button"
                className="button danger small"
                onClick={() => void handleDelete(reply.id)}
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="quick-reply-form">
        <h4>{editingId ? 'Bewerken' : 'Nieuwe quick reply'}</h4>
        <label className="tool-field">
          <span>Shortcut (zonder /)</span>
          <input
            type="text"
            value={shortcut}
            onChange={(event) => setShortcut(event.target.value)}
            placeholder="hallo"
            maxLength={50}
          />
        </label>
        <label className="tool-field">
          <span>Inhoud</span>
          <textarea
            value={content}
            onChange={(event) => setContent(event.target.value)}
            placeholder="Hallo! Bedankt voor je bericht. We nemen zo snel mogelijk contact met je op."
            rows={3}
            maxLength={4096}
          />
        </label>
        <div className="button-row">
          <button
            type="button"
            className="button primary small"
            disabled={saving || !shortcut.trim() || !content.trim()}
            onClick={handleSave}
          >
            {saving ? 'Opslaan…' : editingId ? 'Bijwerken' : 'Toevoegen'}
          </button>
          {editingId && (
            <button
              type="button"
              className="button secondary small"
              onClick={() => {
                setEditingId(null);
                setShortcut('');
                setContent('');
              }}
            >
              Annuleren
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickRepliesPanel;
